/*
 * OUR OWN SIDE CART (2026-10-09; Design E look 2026-10-11). It replaces the Rebuy Smart Cart.
 * Spec: docs/own-cart-build-spec.md.
 *
 * The drawer markup is Liquid: sections/ps-cart.liquid. Every cart change asks Shopify for that
 * section again (the Section Rendering API), so prices, discounts, selling plans and the quantity
 * rules' inputs all come from Liquid. This file only:
 *   - opens and closes the drawer (focus trap, Esc, scroll lock),
 *   - changes quantities and removes lines, one request at a time,
 *   - watches every other script's /cart/add, /cart/change, /cart/update and /cart/clear request
 *     (theme forms, quick add, the subscription builder) and refreshes, opening the drawer on an add,
 *   - applies the three quantity rules that assets/custom.js applied to the Rebuy cart,
 *   - fills the suggestions (see loadRecs): a column beside the drawer on desktop, a shelf at the end
 *     of the item list on the phone, both from the same cards,
 *   - holds Check Out until the ship date and the gift note are saved, 5 seconds at most.
 *
 * The pure functions at the top are exported for node tests (tests/ps-cart.test.mjs).
 */
(function () {
  'use strict';

  // ---------------------------------------------------------------- pure helpers (tested)

  // The three quantity rules from assets/custom.js, as one decision per line.
  //  1. Simmons Building stock cap (variant metafield custom.inventory_levels, location 25491026):
  //     at or over the cap the + button is off and "Max available: N" shows; over it, the line is
  //     set back to N.
  //  2. Products tagged disable-cart-increase, and subscriptions from the builder (_isFromBuilder),
  //     have + off. A builder subscription above 1 is set back to 1.
  //  3. A line property _min_qty is a floor: - is off at the floor, and a line below it is raised.
  // targetQty is null when the line is fine as it is.
  function lineRules(line) {
    var q = Number(line.quantity) || 0;
    var min = parseInt(line.minQty, 10) || 1;
    var target = q;
    var incDisabled = false;
    var message = null;

    var available = parseInt(line.simmonsAvailable, 10);
    if (line.simmonsTracked === true && !isNaN(available)) {
      if (q >= available) message = 'Max available: ' + available;
      if (q > available) target = available;
      incDisabled = q >= available;
    }
    if (line.restricted || line.builderSub) incDisabled = true;
    if (line.builderSub && q > 1) target = 1;
    if (q < min) target = min;
    // Custom bags: "Minimum 25" beside the stepper. The stock cap's message wins when both apply.
    if (!message && min > 1) message = 'Minimum ' + min;

    return {
      incDisabled: incDisabled,
      decDisabled: q <= min,
      message: message,
      targetQty: target !== q ? target : null
    };
  }

  // The quantity one tap of + or − asks for, or null when the rules hold that button off. `line` is
  // lineRules' input, with the quantity already asked for by taps still waiting in the queue.
  function stepQty(line, dir) {
    var r = lineRules(line);
    var q = Number(line.quantity) || 0;
    if (dir === 'inc') return r.incDisabled ? null : q + 1;
    return r.decDisabled ? null : q - 1;
  }

  // What a cart request means for the drawer: 'add' (refresh and open), 'change' (refresh), or
  // null (not a cart write, or one that only saves cart attributes or the note, such as the
  // "Ship date" and "Gift message" saves, which must not redraw the cart).
  function cartRequestKind(url, method, body) {
    if (String(method || 'GET').toUpperCase() !== 'POST') return null;
    var path;
    try {
      path = new URL(String(url), 'https://x.invalid/').pathname;
    } catch (e) {
      return null;
    }
    var m = /\/cart\/(add|change|update|clear)(\.js|\.json)?\/?$/.exec(path);
    if (!m) return null;
    if (m[1] === 'add') return 'add';
    if (m[1] !== 'update') return 'change';
    return updateTouchesItems(body) ? 'change' : null;
  }

  function updateTouchesItems(body) {
    if (body == null) return true;
    if (typeof body === 'string') {
      try {
        var json = JSON.parse(body);
        return !!(json && typeof json === 'object' && json.updates != null);
      } catch (e) {
        return /(^|&)updates/.test(body);
      }
    }
    if (typeof body.keys === 'function') {
      var it = body.keys();
      for (var k = it.next(); !k.done; k = it.next()) {
        if (String(k.value).indexOf('updates') === 0) return true;
      }
      return false;
    }
    return true;
  }

  // Merge recommendation lists, first from each list in turn, then the second from each, and so
  // on. Products already in the cart and repeats are skipped. Returns at most `limit` ids.
  function mergeRecs(lists, excludeIds, limit) {
    var seen = {};
    (excludeIds || []).forEach(function (id) { seen[String(id)] = true; });
    var out = [];
    var longest = 0;
    lists.forEach(function (l) { longest = Math.max(longest, l.length); });
    for (var i = 0; i < longest && out.length < limit; i++) {
      for (var j = 0; j < lists.length && out.length < limit; j++) {
        var id = lists[j][i];
        if (id == null || seen[String(id)]) continue;
        seen[String(id)] = true;
        out.push(String(id));
      }
    }
    return out;
  }

  // THE 3 SUGGESTION PLACES (Phil, 2026-10-16). Each place has one job:
  //   pair   - the first hand-set pair (Search & Discovery "complementary"), else Shopify's related;
  //   coffee - the coffee that best matches the newest coffee in the cart (archive rules, scored in
  //            sections/ps-cart-recs.liquid), else the best-selling coffee;
  //   gap    - while shipping is not free and the gap is known, the item that reaches free shipping
  //            for the least money. It goes first, because it is what the heading offers.
  // The places are chosen gap first (it may be any product, even the pair), then the coffee, then
  // the pair. An empty place is filled from the other lists. No place takes a third product of one
  // kind while another kind is on offer (Codex 2026-10-16).
  // `lists` holds product ids in order; `info[id]` is { price (cents), kind (product type) }.
  function pickRecs(lists, info, inCart, gapCents, limit) {
    var used = {};
    var kinds = {};
    var out = [];
    (inCart || []).forEach(function (id) { used[String(id)] = true; });
    function ok(id) { return id != null && !used[id] && !!info[id]; }
    function roomFor(id) { return (kinds[info[id].kind || ''] || 0) < 2; }
    function take(id) {
      if (id == null) return null;
      used[id] = true;
      var k = info[id].kind || '';
      kinds[k] = (kinds[k] || 0) + 1;
      return id;
    }
    function first(list) {
      for (var i = 0; i < (list || []).length; i++) if (ok(list[i]) && roomFor(list[i])) return list[i];
      return null;
    }
    var all = [].concat(lists.pair || [], lists.related || [], lists.coffee || [], lists.best || [], lists.cheap || []);
    var gap = null;
    if (gapCents > 0) {
      all.forEach(function (id) {
        if (ok(id) && info[id].price >= gapCents && (gap == null || info[id].price < info[gap].price)) gap = id;
      });
      take(gap);
    }
    var coffee = take(first(lists.coffee));
    var pair = take(first(lists.pair) || first(lists.related));
    out = (gap != null ? [gap, pair, coffee] : [pair, coffee]).filter(function (id) { return id != null; });
    var fill = [].concat(lists.pair || [], lists.related || [], lists.best || [], lists.coffee || [], lists.cheap || []);
    [false, true].forEach(function (anyKind) {
      fill.forEach(function (id) {
        if (out.length >= limit || !ok(id)) return;
        if (!anyKind && (kinds[info[id].kind || ''] || 0) >= 2) return;
        out.push(take(id));
      });
    });
    return out.slice(0, limit);
  }

  // The variant of the newest coffee line (Filter, Espresso or Decaf), or ''. The coffee place is
  // scored against it; it is in the request so each cart's answer is cached on its own.
  function coffeeLine(items) {
    for (var i = 0; i < (items || []).length; i++) {
      if (/^(filter|espresso|decaf)$/i.test(String(items[i].product_type || ''))) return String(items[i].variant_id);
    }
    return '';
  }

  // The products to ask recommendations for: newest line first, each product once.
  function recInputs(items, max) {
    var out = [];
    (items || []).forEach(function (i) {
      var id = String(i.product_id);
      if (out.indexOf(id) === -1) out.push(id);
    });
    return out.slice(0, max);
  }

  // ---- the shipping card (Design E, build spec sections 3 and 5)

  // A money string's number: "$2.05" -> 2.05, "from $4.10 to $6.00" -> 4.1. Not a number -> null,
  // never 0 (Rebuy's header read "from $x" as $0 and showed free shipping).
  function parseMoney(s) {
    var m = /(\d[\d,]*(?:\.\d+)?)/.exec(String(s == null ? '' : s));
    return m ? Number(m[1].replace(/,/g, '')) : null;
  }

  // The shipping-rates worker's "cart" text, "⏱️ Order within 3 hrs<br>📦 Delivery Today by 7 PM",
  // as two plain lines in sentence case.
  function parseDelivery(text) {
    var out = { line: '', within: '' };
    String(text || '').split(/<br\s*\/?>/i).forEach(function (part) {
      var t = part.replace(/<[^>]*>/g, '').replace(/^[^A-Za-z0-9]+/, '').replace(/\s+/g, ' ').trim();
      if (!t) return;
      if (/^order within/i.test(t)) out.within = t;
      else if (!out.line) out.line = t.replace(/\b(Today|Tomorrow)\b/g, function (w) { return w.toLowerCase(); });
    });
    return out;
  }

  var DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // "Ships tomorrow" from the worker's ship date ("YYYY-MM-DD", Mountain time) and today's date there.
  function shipsLine(shipDate, today) {
    var a = /^(\d{4})-(\d{2})-(\d{2})$/.exec(shipDate || '');
    var b = /^(\d{4})-(\d{2})-(\d{2})$/.exec(today || '');
    if (!a || !b) return '';
    var d = Date.UTC(+a[1], a[2] - 1, +a[3]);
    var days = Math.round((d - Date.UTC(+b[1], b[2] - 1, +b[3])) / 86400000);
    var when = new Date(d);
    if (days <= 0) return 'Ships today';
    if (days === 1) return 'Ships tomorrow';
    if (days < 7) return 'Ships ' + DAYS[when.getUTCDay()];
    return 'Ships ' + MONTHS[when.getUTCMonth()] + ' ' + when.getUTCDate();
  }

  // A carrier note from shipping-service ("We're back with DHL..."). It shows as an info row with
  // its own icon (Phil, 2026-10-12, design A), so the emoji typed around it are dropped.
  function shipNote(text) {
    if (!text) return '';
    return String(text).replace(/[\p{Extended_Pictographic}\u{1F1E6}-\u{1F1FF}\uFE0F\u200D]/gu, '').replace(/\s+/g, ' ').trim();
  }

  // What the shipping card shows for one worker answer. state: 'paid', 'free' or 'unavailable'.
  // The percent is the share of the SHIPPING price saved. Never a guessed original price: until the
  // worker sends originalPriceCents, no price is crossed out.
  function shippingView(data, today) {
    var none = { state: 'unavailable' };
    if (!data || typeof data !== 'object' || data.error || data.available === false) return none;
    // The worker turns "no carrier serves this address" into $0.00 and 100% with no ship date and
    // no delivery text, so that means no price. A real quote has a ship date, or at least its
    // delivery text (a gift card by mail is free and has no ship date: "Gift Card by Mail").
    if (!data.shipDate && !data.cart && !data.deliveryLine) return none;
    var paid = typeof data.priceCents === 'number' ? data.priceCents / 100 : parseMoney(data.shippingCost);
    if (paid == null || isNaN(paid)) return none;
    var original = data.originalPriceCents > 0 ? data.originalPriceCents / 100 : null;
    if (original != null && original < paid) original = null;
    var free = paid === 0;
    var pct = original != null ? Math.round((1 - paid / original) * 100) : (parseInt(data.discountPercent, 10) || 0);
    pct = free ? 100 : Math.max(0, Math.min(99, pct));
    var delivery = data.deliveryLine
      ? { line: String(data.deliveryLine), within: String(data.orderWithin || '') }
      : parseDelivery(data.cart);
    if (!delivery.line) delivery.line = shipsLine(data.shipDate, today);
    // Within $20 of free, shipping-service adds freeShippingGapCents (it owns the $20 rule; the
    // cart applies none of its own). Record: shipping-service/DECISIONS.md, "The cart says $X away
    // from free shipping within $20".
    var gapCents = data.freeShippingGapCents;
    var gap = !free && typeof gapCents === 'number' && gapCents > 0 ? gapCents / 100 : null;
    return {
      state: free ? 'free' : 'paid',
      paid: paid,
      original: original,
      pct: pct,
      gap: gap,
      gapEstimate: data.freeShippingGapIsEstimate !== false,
      estimate: !!data.estimate,
      delivery: delivery,
      shipDate: /^\d{4}-\d{2}-\d{2}$/.test(data.shipDate || '') ? data.shipDate : null,
      message: shipNote(data.cartMessage)
    };
  }

  // The headline of the shipping panel (Agata's version 2, Phil 2026-10-15): one line above the bar.
  // Within the free-shipping window shipping-service sends freeShippingGapCents (it owns the rule; the
  // cart applies none of its own): "Free delivery in $18.94" when exact (courier), "Free shipping in
  // about $3" when estimated (parcels, whole dollars rounded up). Otherwise "You're saving 70% on
  // shipping"; free is "Shipping's on us!". null when there is nothing to say.
  function shipHeadline(view) {
    if (!view) return null;
    if (view.state === 'free') return { before: '', n: '', after: '', free: 'Shipping\u2019s on us!', said: 'Free shipping' };
    if (view.gap != null) {
      var exact = !view.gapEstimate;
      var n = '$' + (exact ? view.gap.toFixed(2) : String(Math.ceil(view.gap)));
      var before = exact ? 'Free delivery in ' : 'Free shipping in about ';
      return { before: before, n: n, after: '', free: '', said: before + n };
    }
    if (view.pct > 0) return { before: 'You\u2019re saving ', n: view.pct + '%', after: ' on shipping', free: '', said: view.pct + '% saved on shipping' };
    return null;
  }

  // "Order within 3 hrs and 48 mins" reads "Order in 3 hrs 48 mins" beside the delivery promise.
  function withinLine(text) {
    var t = String(text || '').trim();
    // Phil 2026-10-15 (option 2): "Order in 21 hrs 10 mins", short enough to stay on the right of
    // "Delivery tomorrow by 7 PM" and "Ships tomorrow".
    return t.replace(/^order within\b/i, 'Order in').replace(/(\d+ hrs?) and (\d+ mins?)/i, '$1 $2');
  }

  // ---------------------------------------------------------------- a line with its own ship date
  // SHIPPING-SERVICE HOLDS THE WHOLE ORDER until a line's date (shipping-service/src/shipDate.js,
  // sdSubscriptionDay rule 1): the first line with a property whose name has "ship date" (any case,
  // a leading "_" too), and only a day after the order day. Project III (Dec 11) and Coffee with
  // Your Logo (the buyer's Friday) carry one, so everything else waits too. The cart says so (Agata,
  // "Project III reminder", 2026-10-26; Phil chose option A, notice only). These rules copy the
  // service's; change both together. Rule 1's second kind, "Gift Subscription Start Date", is left
  // out on purpose: the cart's quote (checkoutRates.js crGetShipDate) does not read it, so the box
  // and the delivery row would name two different days (Codex 2026-10-26).

  var MONTH_NAMES = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];

  function validDay(y, m, d) {
    var t = new Date(Date.UTC(y, m - 1, d));
    if (t.getUTCFullYear() !== y || t.getUTCMonth() !== m - 1 || t.getUTCDate() !== d) return null;
    return y + '-' + String(m).padStart(2, '0') + '-' + String(d).padStart(2, '0');
  }

  // A typed date to a Calgary day ("YYYY-MM-DD"), or null: sdCustomerDay in shipping-service, case
  // for case. "2026-12-11", "2026/12/11", "12/11/2026" (month first), an instant with a time
  // ("2026-12-11T10:00:00-07:00", its Calgary day), "Dec 11, 2026", "Sept. 5, 2026". A plain date is
  // never run through a Date, which reads it as UTC midnight (a day early) and rolls Feb 30 into March.
  function shipDay(value) {
    var s = String(value == null ? '' : value).trim();
    if (!s) return null;
    var iso = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/.exec(s);
    if (iso) return validDay(+iso[1], +iso[2], +iso[3]);
    var us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
    if (us) return validDay(+us[3], +us[1], +us[2]);
    if (/\d:\d/.test(s)) {
      var t = new Date(s);
      if (isNaN(t.getTime())) return null;
      try {
        return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Edmonton', year: 'numeric', month: '2-digit', day: '2-digit' }).format(t);
      } catch (e) { return null; }
    }
    var named = /^([A-Za-z]{3,9})\.?\s+(\d{1,2}),?\s+(\d{4})$/.exec(s);
    if (!named) return null;
    var word = named[1].toLowerCase();
    var month = MONTH_NAMES.findIndex(function (m) { return m.indexOf(word) === 0; });
    return month < 0 ? null : validDay(+named[3], month + 1, +named[2]);
  }

  function shortDay(day) {
    var p = day.split('-');
    return MONTHS[+p[1] - 1] + ' ' + (+p[2]);
  }

  // The line's later day for one kind of property name, or null.
  function lineDay(item, wanted, today) {
    var props = (item && item.properties) || {};
    for (var key in props) {
      if (!wanted(key)) continue;
      var day = shipDay(props[key]);
      if (day && day > today) return day;
    }
    return null;
  }

  // "A", "A and B", "A, B and C".
  function joinNames(names) {
    if (names.length < 2) return names[0] || '';
    return names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1];
  }

  // What the cart says, from the cart's items, today ("YYYY-MM-DD", Mountain time) and the quote's
  // ship day when it has come (quotedDay, the day shipping-service will ship: it owns that fact and
  // can move a line's date later for a product's lead time). null when no line has a later date.
  // Otherwise { notice, lines, conflict }: notice is the box above the list (null when nothing
  // else waits), lines maps a line key to { tag } (the dated line) or { note } (a line that waits).
  // conflict: the quote ships SOONER than a dated line, so the delivery row is withheld and the box
  // keeps the line's own day. A gift card by email ships nothing, so it never waits.
  function holdView(items, today, quotedDay) {
    var shipped = (items || []).filter(function (i) { return i && i.requires_shipping !== false; });
    var dated = [];
    shipped.forEach(function (i) {
      var day = lineDay(i, function (k) { return /ship date/i.test(k); }, today);
      if (day) dated.push({ item: i, day: day });
    });
    if (!dated.length) return null;
    var others = shipped.filter(function (i) { return !dated.some(function (d) { return d.item === i; }); });
    var name = function (i) { return i.product_title || i.title || 'An item'; };
    var unique = function (list) { return list.filter(function (v, n) { return list.indexOf(v) === n; }); };
    // The quote ships the whole order on the LATEST dated line's day, so a line with an earlier date
    // waits like any other line and is named with its own date (Phil, 2026-10-26).
    var latest = dated.reduce(function (a, d) { return d.day > a ? d.day : a; }, '');
    var holders = dated.filter(function (d) { return d.day === latest; });
    var earlier = dated.filter(function (d) { return d.day < latest; });
    // One product on two dates: the later line carries its day, so the two lines are told apart.
    var split = function (d) { return earlier.some(function (e) { return name(e.item) === name(d.item); }); };
    var holderNames = unique(holders.map(function (d) { return split(d) ? name(d.item) + ' (' + shortDay(d.day) + ')' : name(d.item); }));
    var plural = holderNames.length > 1;

    // An exact day only once the quote has named it; before that (or when it fails, or ships before
    // a dated line) the line's own day is a floor, "at the earliest" (Codex 2026-10-26).
    var conflict = !!quotedDay && quotedDay < latest;
    var exact = !!quotedDay && !conflict;
    var when = shortDay(exact ? quotedDay : latest);
    var lines = {};
    holders.forEach(function (d) {
      // The box names a split line by its own date, so its tag says that date too, even when the
      // date property is private or the quote moved the day (Codex 2026-10-26).
      var own = split(d) && shortDay(d.day) !== when ? shortDay(d.day) + ' date, ships ' : (exact ? 'Ships ' : 'Earliest ');
      lines[d.item.key] = { tag: own + when };
    });
    var waiting = earlier.map(function (d) { return d.item; }).concat(others);
    if (!waiting.length) return { conflict: conflict, lines: lines, notice: null };

    var otherNames = unique(others.map(name));
    var otherCount = others.reduce(function (n, i) { return n + (i.quantity || 1); }, 0);
    var waitNames = unique(earlier.map(function (d) { return name(d.item) + ' (' + shortDay(d.day) + ' date)'; }))
      .concat(otherNames.length > 2 ? ['the other ' + otherCount + ' items in your cart'] : otherNames);
    var waits = joinNames(waitNames) + (waitNames.length === 1 && otherNames.length < 3 ? ' waits' : ' wait');
    // Short, so it stays on one line under any coffee name: the box above says why.
    waiting.forEach(function (i) { lines[i.key] = { note: (exact ? 'Waits until ' : 'Waits, earliest ') + when }; });
    return {
      conflict: conflict,
      lines: lines,
      notice: {
        title: exact ? 'Your whole order ships ' + when : 'Your whole order waits for ' + joinNames(holderNames),
        body: joinNames(holderNames) + (plural ? ' ship ' : ' ships ') + when + (exact ? '' : ' at the earliest') + '. We send your order in one box, so ' + waits + ' until then too.',
        tip: 'Want ' + (waitNames.length === 1 && otherNames.length < 3 ? waitNames[0] : 'them') + ' sooner? Remove ' + joinNames(holderNames) + ', check out, then order ' + (plural ? 'them on their own.' : 'it on its own.')
      }
    };
  }

  // The shipping card's delivery row when a line has its own ship date: the countdown ("Order in 45
  // days and 20 hrs") means nothing then, and "Whole order:" says the rest waits too. When the quote
  // ships before a dated line there is no true promise, so the row goes (an empty line hides it).
  function holdDelivery(hold, delivery) {
    if (!hold) return delivery;
    if (hold.conflict) return { line: '', within: '' };
    var line = delivery.line || '';
    if (hold.notice && line) line = 'Whole order: ' + line.charAt(0).toLowerCase() + line.slice(1);
    return { line: line, within: '' };
  }

  function todayMountain() {
    try {
      return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Edmonton', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
    } catch (e) {
      return '';
    }
  }

  // Money in the cart's currency ("$73.70" for CAD), the one formatter for the cart and the panel.
  function formatMoney(cents, code) {
    var fmt = function (c) { return new Intl.NumberFormat('en', { style: 'currency', currency: c, currencyDisplay: 'narrowSymbol' }).format(cents / 100); };
    try { return fmt(code || 'CAD'); } catch (e) { return fmt('CAD'); }
  }

  // The summary under the panel's line: the Shipping row and the Check Out label (Phil 2026-10-15:
  // the total says "+ tax", because tax is added at checkout). An estimated shipping price makes the
  // total a "From" total. Without a known shipping price the button says only "Check out", so it
  // never shows a total that leaves shipping out.
  //   totalCents: the cart total; ship: { state, paid (dollars), estimate }; code: the currency.
  function cartSummary(totalCents, ship, code) {
    var m = function (c) { return formatMoney(c, code); };
    var state = ship && ship.state;
    if (state === 'free') return { ship: 'FREE', button: 'Check out \u00b7 ' + m(totalCents) + ' + tax' };
    if (state === 'paid' && typeof ship.paid === 'number') {
      var paid = Math.round(ship.paid * 100);
      var from = ship.estimate ? 'From ' : '';
      return { ship: from + m(paid), button: 'Check out \u00b7 ' + from + m(totalCents + paid) + ' + tax' };
    }
    if (state === 'none') return { ship: null, button: 'Check out \u00b7 ' + m(totalCents) + ' + tax' };
    if (state === 'loading') return { ship: 'Checking\u2026', button: 'Check out' };
    return { ship: 'At checkout', button: 'Check out' };
  }

  // One id per browser, sent with each price check and saved on the cart as "__ps_cart_session", so
  // carts can be joined to orders for A/B tests later. shipping-service accepts letters, digits and
  // hyphens, 8 to 64 characters; anything else is replaced.
  var SESSION_KEY = 'ps_cart_session';
  var sessionMemo = null;
  function cartSessionId(storage, make) {
    var id = null;
    try { id = storage.getItem(SESSION_KEY); } catch (e) { /* blocked */ }
    if (id && /^[A-Za-z0-9-]{8,64}$/.test(id)) return id;
    id = sessionMemo || make();
    sessionMemo = id;
    try { storage.setItem(SESSION_KEY, id); } catch (e) { /* blocked: this page keeps it */ }
    return id;
  }

  // The suggestions heading follows the shipping card (build spec section 3).
  // An empty cart sees the store's best sellers, so its line says so.
  function recsCopy(state) {
    if (state === 'paid' || state === 'unavailable') return { title: 'Add more and save on shipping', line: '' };
    if (state === 'empty') return { title: 'You might also like', line: 'What people are ordering most right now.' };
    return { title: 'You might also like', line: '' };
  }

  // GIFT NOTE (Phil, 2026-10-11). Three cart attributes:
  //   "Gift message"   printed on the card (the order scripts read it; do not rename it),
  //   "Gift recipient" the "To" name the shopper types (read before the shipping-name guess),
  //   "Gift sender"    the "From" name, printed as "A GIFT FROM ..." (both order scripts already
  //                    read it; blank falls back to the billing first name, as before).
  // No message means no note, so both names are cleared with it.
  function giftValue(v) {
    v = v || {};
    var m = String(v.message || '');
    var name = function (x) { return String(x || '').replace(/\s+/g, ' ').trim(); };
    if (!m.trim()) return { to: '', from: '', message: '' };
    return { to: name(v.to), from: name(v.from), message: m };
  }
  function giftAttributes(v) {
    return { 'Gift message': v.message, 'Gift recipient': v.to, 'Gift sender': v.from };
  }

  var exported = {
    lineRules: lineRules,
    stepQty: stepQty,
    cartRequestKind: cartRequestKind,
    mergeRecs: mergeRecs,
    pickRecs: pickRecs,
    coffeeLine: coffeeLine,
    recInputs: recInputs,
    parseMoney: parseMoney,
    parseDelivery: parseDelivery,
    shipsLine: shipsLine,
    shippingView: shippingView,
    shipHeadline: shipHeadline,
    withinLine: withinLine,
    shipDay: shipDay,
    holdView: holdView,
    holdDelivery: holdDelivery,
    cartSummary: cartSummary,
    formatMoney: formatMoney,
    cartSessionId: cartSessionId,
    recsCopy: recsCopy,
    giftValue: giftValue,
    giftAttributes: giftAttributes
  };
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = exported;
    return;
  }
  if (typeof window === 'undefined' || window.PSCart) return;

  // ---------------------------------------------------------------- browser

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var SECTION = 'ps-cart';
  var RECS_SECTION = 'ps-cart-recs';
  var RECS_LIMIT = 3;            // the Rebuy widget showed 3
  var RECS_INPUTS = 3;           // ask about the 3 newest products in the cart
  var RECS_TTL = 30 * 60 * 1000; // keep a recommendation answer 30 minutes in this tab
  var CHECKOUT_WAIT = 5000;      // Check Out never waits longer than this

  var nativeFetch = window.fetch.bind(window);
  var cart = readInitialCart();
  var root = null;               // the .ps-cart element
  var lastFocus = null;
  var queue = Promise.resolve(); // cart writes run one after another
  var pending = 0;
  var fixTries = {};             // quantity fixes sent per line since it was last fine (at most 2)
  var wantQty = {};              // per line, the quantity quick taps have asked for, until the queue empties
  var inflight = [];             // other scripts' cart writes, and the refresh each one starts
  var seq = 0;                   // every cart answer is numbered when its request starts...
  var appliedSeq = 0;            // ...and an answer older than the one on screen is ignored
  var active = 0;                // cart requests on the wire now (ours and other scripts' writes)
  var reconcile = false;         // two were on the wire at once, so draw the cart once more when quiet
  var recsKey = null;
  var recsData = null;           // the suggestion lists last fetched, for redrawing when the gap changes
  var recsGap = null;            // the free-shipping gap (cents) the suggestions were drawn for
  var recsTimer = null;
  var checkingOut = false;
  var shipState = 'paid';        // the shipping card's state, for the suggestions heading
  var shipInfo = { state: 'loading' }; // { state, paid, estimate, gap } for the summary, Check Out and suggestions
  var summaryKey = null;         // the cart the shipping quote belongs to

  function readInitialCart() {
    var el = document.getElementById('PSCartData');
    try {
      return el ? JSON.parse(el.getAttribute('data-json')) : { items: [], item_count: 0, currency: '' };
    } catch (e) {
      return { items: [], item_count: 0, currency: '' };
    }
  }

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function emit(name, detail) {
    var ev;
    try {
      ev = new CustomEvent(name, { detail: detail });
    } catch (e) {
      ev = document.createEvent('CustomEvent');
      ev.initCustomEvent(name, false, false, detail);
    }
    document.dispatchEvent(ev);
  }

  function announce(text) {
    var live = root && $('[data-ps-cart-live]', root);
    if (!live) return;
    live.textContent = '';
    setTimeout(function () { live.textContent = text; }, 50);
  }

  // ---------------------------------------------------------------- rendering

  function sectionUrl() {
    return ROOT + '?sections=' + SECTION;
  }

  // Swap in the parts of a fresh copy of the section. The shipping header, the gift note and the
  // delivery timer are NOT parts: they keep their own state and are never redrawn.
  function applySection(html, n) {
    if (!html || !root) return;
    // Requests are numbered when they START, which is not the order the cart changed in. So an
    // ignored answer is never trusted to be the old one: once every write has settled, the cart is
    // drawn again from a fresh request (whenIdle), and that one is final.
    if (n < appliedSeq) { reconcile = true; return; }
    appliedSeq = n;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var focus = rememberFocus();
    $$('[data-ps-cart-part]', doc).forEach(function (fresh) {
      var name = fresh.getAttribute('data-ps-cart-part');
      var old = $('[data-ps-cart-part="' + name + '"]', root);
      if (old) old.replaceWith(document.importNode(fresh, true));
    });
    var data = $('[data-ps-cart-part="data"]', root);
    if (data) {
      try { cart = JSON.parse(data.getAttribute('data-json')); } catch (e) { /* keep the last good cart */ }
    }
    afterRender();
    restoreFocus(focus);
  }

  function afterRender() {
    updateCount();
    root.classList.toggle('is-empty', !cart.item_count);
    if (!cart.item_count) setRecsCopy();
    updateMore();
    renderSummary();
    applyRules();
    quotedDay = null;
    renderHold();
    emit('ps-cart:change', { cart: cart });
    scheduleRecs();
  }

  function updateCount() {
    $$('[data-js-cart-count]').forEach(function (el) { el.textContent = cart.item_count; });
  }

  // Only focus inside a redrawn part is lost; focus anywhere else (the gift note box) is left alone.
  function rememberFocus() {
    var a = document.activeElement;
    if (!a || !root || !root.contains(a) || !a.closest('[data-ps-cart-part]')) return null;
    var line = a.closest('[data-ps-line]');
    return {
      key: line && line.getAttribute('data-key'),
      control: a.getAttribute('data-ps-qty') || (a.hasAttribute('data-ps-remove') ? 'remove' : null)
    };
  }

  function restoreFocus(f) {
    if (!f || !root) return;
    var target = null;
    if (f.key) {
      var line = $('[data-ps-line][data-key="' + cssEscape(f.key) + '"]', root);
      if (line && f.control === 'remove') target = $('[data-ps-remove]', line);
      else if (line && f.control) target = $('[data-ps-qty="' + f.control + '"]', line);
      if (target && target.disabled) target = $('[data-ps-qty]:not([disabled])', line) || $('[data-ps-remove]', line);
    }
    if (!target && root.classList.contains('is-open')) target = $('[data-ps-cart-close]', root);
    if (target) target.focus();
  }

  function cssEscape(s) {
    return window.CSS && CSS.escape ? CSS.escape(s) : String(s).replace(/["\\]/g, '\\$&');
  }

  // ---------------------------------------------------------------- quantity rules

  // lineRules' input for one drawn line; `qty` replaces the quantity on screen when given.
  function lineInput(li, qty) {
    return {
      quantity: qty != null ? qty : li.getAttribute('data-qty'),
      minQty: li.getAttribute('data-min-qty'),
      simmonsTracked: li.getAttribute('data-simmons-tracked') === 'true',
      simmonsAvailable: li.getAttribute('data-simmons-available'),
      restricted: li.getAttribute('data-restricted') === 'true',
      builderSub: li.getAttribute('data-builder-sub') === 'true'
    };
  }

  // The ship-date box above the list and the tag or note on each line (holdView). Both sit inside
  // redrawn parts, so every draw starts from the hidden, empty slots.
  // The quote's ship day, set by the shipping card (setQuotedDay) and dropped on every cart change
  // until the new quote comes, so the box never shows an older cart's day.
  var quotedDay = null;
  function setQuotedDay(day) {
    day = day || null;
    if (day === quotedDay) return;
    quotedDay = day;
    renderHold();
  }

  function renderHold() {
    if (!root) return;
    var hold = holdView(cart.items, todayMountain(), quotedDay);
    var box = $('[data-ps-hold]', root);
    if (box) {
      box.hidden = !(hold && hold.notice);
      if (!box.hidden) {
        $('[data-ps-hold-title]', box).textContent = hold.notice.title;
        $('[data-ps-hold-body]', box).textContent = hold.notice.body;
        $('[data-ps-hold-tip]', box).textContent = hold.notice.tip;
      }
    }
    $$('[data-ps-line]', root).forEach(function (li) {
      var said = hold && hold.lines[li.getAttribute('data-key')];
      var slot = $('[data-ps-line-hold]', li);
      if (!slot) return;
      slot.hidden = !said;
      slot.textContent = said ? (said.tag || said.note) : '';
      slot.classList.toggle('is-tag', !!(said && said.tag));
      // The tag says the same as the raw "Ship Date: Dec 11, 2026" line, so that line steps aside.
      $$('[data-ps-ship-prop]', li).forEach(function (p) { p.hidden = !!(said && said.tag); });
    });
  }

  function applyRules() {
    if (!root) return;
    var fixes = [];
    $$('[data-ps-line]', root).forEach(function (li) {
      var r = lineRules(lineInput(li));
      var inc = $('[data-ps-qty="inc"]', li);
      var dec = $('[data-ps-qty="dec"]', li);
      if (inc) { inc.disabled = r.incDisabled; inc.setAttribute('aria-disabled', String(r.incDisabled)); }
      if (dec) { dec.disabled = r.decDisabled; dec.setAttribute('aria-disabled', String(r.decDisabled)); }
      var msg = $('[data-ps-line-message]', li);
      if (msg) {
        msg.textContent = r.message || '';
        msg.hidden = !r.message;
      }
      var key = li.getAttribute('data-key');
      if (r.targetQty == null) {
        delete fixTries[key];   // fine now, so a later breach is corrected again
      } else if ((fixTries[key] || 0) < 2) {
        fixTries[key] = (fixTries[key] || 0) + 1;
        fixes.push({ key: key, qty: r.targetQty });
      }
    });
    fixes.forEach(function (f) { changeLine(f.key, f.qty); });
  }

  // ---------------------------------------------------------------- cart writes

  function enqueue(task) {
    pending++;
    setBusy(true);
    var run = queue.then(task, task);
    queue = run.catch(function () {}).then(function () {
      pending--;
      if (!pending) { setBusy(false); wantQty = {}; }
      whenIdle();
    });
    return run;
  }

  function setBusy(on) {
    if (root) root.classList.toggle('is-busy', on);
    var flyout = root && $('.ps-cart__flyout', root);
    if (flyout) flyout.setAttribute('aria-busy', on ? 'true' : 'false');
  }

  function postJSON(path, body) {
    return nativeFetch(ROOT + path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (json) {
        if (!res.ok) {
          var err = new Error(json.description || json.message || ('HTTP ' + res.status));
          err.status = res.status;
          throw err;
        }
        return json;
      });
    });
  }

  function changeLine(key, quantity, button) {
    var line = root && $('[data-ps-line][data-key="' + cssEscape(key) + '"]', root);
    if (button) button.classList.add('is-working');
    if (line) line.classList.add('is-working');
    return enqueue(function () {
      var n = ++seq;
      return counted(postJSON('cart/change.js', { id: key, quantity: quantity, sections: SECTION, sections_url: ROOT }))
        .then(function (res) {
          return res.sections && res.sections[SECTION] ? applySection(res.sections[SECTION], n) : refresh();
        })
        .then(function () {
          announce(quantity === 0 ? 'Item removed. ' + countText() : 'Cart updated. ' + countText());
        })
        .catch(function (err) {
          return refresh().then(function () { showLineError(key, err.message); });
        });
    });
  }

  function addVariant(variantId, quantity, properties) {
    return enqueue(function () {
      var n = ++seq;
      var item = { id: Number(variantId), quantity: quantity || 1 };
      if (properties) item.properties = properties;
      return counted(postJSON('cart/add.js', {
        items: [item],
        sections: SECTION,
        sections_url: ROOT
      })).then(function (res) {
        return res.sections && res.sections[SECTION] ? applySection(res.sections[SECTION], n) : refresh();
      }).then(function () {
        announce('Added to cart. ' + countText());
      });
    });
  }

  function refresh() {
    var n = ++seq;
    return counted(nativeFetch(sectionUrl(), { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); }))
      .then(function (json) { applySection(json[SECTION], n); })
      .catch(function (e) { console.warn('[PSCart] refresh failed', e); });
  }

  function countText() {
    return cart.item_count === 1 ? '1 item in your cart.' : cart.item_count + ' items in your cart.';
  }

  function showLineError(key, text) {
    var line = root && $('[data-ps-line][data-key="' + cssEscape(key) + '"]', root);
    var el = line && $('[data-ps-line-error]', line);
    var message = text || 'Sorry, that did not work. Please try again.';
    if (el) {
      el.textContent = message;
      el.hidden = false;
    }
    announce(message);
  }

  // ---------------------------------------------------------------- watch other scripts' cart calls

  function onCartWrite(kind) {
    if (!kind) return null;
    var p = refresh();
    if (kind === 'add') p.then(open);
    return p;
  }

  // Check Out waits for these: the other script's write, then the refresh it starts.
  function track(p) {
    var t = p.then(done, done);
    function done() {
      var i = inflight.indexOf(t);
      if (i !== -1) inflight.splice(i, 1);
      whenIdle();
    }
    inflight.push(t);
    return t;
  }

  // Nothing queued and nothing in flight: if an answer was ignored on the way, draw the cart from a
  // fresh request now. Check Out's settle() waits for this refresh too, as it is tracked.
  function whenIdle() {
    if (active || pending || inflight.length || !reconcile) return;
    reconcile = false;
    track(refresh());
  }

  // Every cart request is counted while it is on the wire. Two at once means an answer may show a
  // cart that another request has already changed, whatever order they come back in. Then, when
  // everything is quiet, one fresh request alone draws the final cart (whenIdle).
  function begin() {
    active++;
    if (active > 1) reconcile = true;
  }
  function end() {
    active = Math.max(0, active - 1);
    setTimeout(whenIdle, 0);
  }
  function counted(p) {
    begin();
    return p.then(function (v) { end(); return v; }, function (e) { end(); throw e; });
  }

  // Resolves once no cart write is queued or in flight, including fixes queued while waiting.
  function settle() {
    var q = queue;
    return Promise.all([q].concat(inflight)).then(function () {
      if (q !== queue || inflight.length) return settle();
      // A request still on the wire, or the final redraw about to start: look again shortly.
      if (active || reconcile) return new Promise(function (r) { setTimeout(r, 50); }).then(settle);
    });
  }

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || String(input);
    var method = (init && init.method) || (input && typeof input === 'object' && input.method) || 'GET';
    var kind = cartRequestKind(url, method, init && init.body);
    var result = nativeFetch.apply(window, arguments);
    if (kind) {
      track(counted(result).then(function (res) { return res && res.ok ? onCartWrite(kind) : null; }));
    }
    return result;
  };

  (function watchXHR() {
    var X = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
    if (!X) return;
    var open = X.open;
    var send = X.send;
    X.open = function (method, url) {
      this._psCart = { method: method, url: url };
      return open.apply(this, arguments);
    };
    X.send = function (body) {
      var info = this._psCart;
      var kind = info ? cartRequestKind(info.url, info.method, body) : null;
      if (kind) {
        var xhr = this;
        var finished = false;
        var settleXhr;
        begin();
        track(new Promise(function (resolve) { settleXhr = resolve; }));
        var onEnd = function () {
          xhr.removeEventListener('loadend', onEnd);
          if (finished) return;
          finished = true;
          end();
          settleXhr(xhr.status >= 200 && xhr.status < 300 ? onCartWrite(kind) : null);
        };
        xhr.addEventListener('loadend', onEnd);
        try {
          return send.apply(this, arguments);
        } catch (e) {
          // send() threw before the request left: release the count, then let the error through.
          xhr.removeEventListener('loadend', onEnd);
          if (!finished) { finished = true; end(); settleXhr(null); }
          throw e;
        }
      }
      return send.apply(this, arguments);
    };
  })();

  // ---------------------------------------------------------------- open / close

  function open() {
    if (!root || root.classList.contains('is-open')) return;
    lastFocus = document.activeElement;
    root.hidden = false;
    root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('ps-cart-lock');
    document.addEventListener('touchmove', blockTouch, { passive: false });
    // Read a size first, so the browser lays out the closed drawer and the slide-in transition
    // runs. (Waiting for animation frames instead left the drawer shut in a background tab.)
    void root.offsetWidth;
    root.classList.add('is-open');
    placeSide();
    var close = $('[data-ps-cart-close].ps-cart__close', root);
    // Focus moves into the cart for keyboards and screen readers, without a ring after a mouse click.
    if (close) close.focus({ preventScroll: true, focusVisible: false });
    emit('ps-cart:open', { cart: cart });
  }

  function close() {
    if (!root || !root.classList.contains('is-open')) return;
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('ps-cart-lock');
    document.removeEventListener('touchmove', blockTouch, { passive: false });
    setTimeout(function () { if (!root.classList.contains('is-open')) root.hidden = true; }, 260);
    if (lastFocus && typeof lastFocus.focus === 'function' && document.contains(lastFocus)) lastFocus.focus();
    emit('ps-cart:close', { cart: cart });
  }

  // iOS Safari scrolls the page behind an overflow:hidden body. Touch scrolling is allowed only
  // inside the cart's list, a text box, the city search and the gift note (it lives in the header
  // since 2026-10-15, outside the bottom panel).
  function blockTouch(e) {
    var t = e.target;
    if (t && t.closest && t.closest('.ps-cart__list, .ps-cart__side, .ps-cart__bottom, .ps-gift__panel, textarea, select, gmp-place-autocomplete, #autocomplete-wrapper')) return;
    if (e.cancelable) e.preventDefault();
  }

  // An open phone sheet (city search, gift note) is the whole screen, so Tab stays inside it.
  function openSheet() {
    return $$('.is-sheet', root).filter(function (el) { return !el.hidden; })[0] || null;
  }

  function trapTab(e) {
    var flyout = openSheet() || $('.ps-cart__dialog', root);
    // Google's city search keeps its input in a shadow root: the element itself stands for it.
    var items = $$('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex], gmp-place-autocomplete', flyout)
      .filter(function (el) { return el.getAttribute('tabindex') !== '-1'; })
      .filter(function (el) { return !el.hidden && el.getClientRects().length > 0; });   // fixed sheets have no offsetParent
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (e.shiftKey && (document.activeElement === first || !flyout.contains(document.activeElement))) {
      e.preventDefault();
      focusIn(last);
    } else if (!e.shiftKey && (document.activeElement === last || !flyout.contains(document.activeElement))) {
      e.preventDefault();
      focusIn(first);
    }
  }

  function focusIn(el) {
    var inner = el.localName === 'gmp-place-autocomplete' && el.shadowRoot && el.shadowRoot.querySelector('input');
    (inner || el).focus();
  }

  function isCartLink(a) {
    if (!a || !a.getAttribute) return false;
    if (a.hasAttribute('data-js-cart-icon')) return true;
    var href = a.getAttribute('href');
    if (!href || a.target === '_blank') return false;
    try {
      var u = new URL(href, window.location.href);
      return u.origin === window.location.origin && u.pathname.replace(/\/$/, '') === (ROOT + 'cart').replace(/\/+/g, '/');
    } catch (e) {
      return false;
    }
  }

  // ---------------------------------------------------------------- Check Out

  // This browser's id (cartSessionId above), from localStorage when it can be read.
  function browserSessionId() {
    var store = null;
    try { store = window.localStorage; } catch (e) { /* blocked */ }
    var makeId = function () {
      return (window.crypto && typeof window.crypto.randomUUID === 'function')
        ? window.crypto.randomUUID()
        : Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12);
    };
    return cartSessionId(store || { getItem: function () { return null; }, setItem: function () {} }, makeId);
  }

  // Counts Check Out clicks for the cart shipping analytics (shipping admin, "Cart shipping").
  // text/plain, because a beacon cannot send JSON without a preflight. Fire and forget: it never
  // delays Check Out. Record: shipping-service DECISIONS, 2026-10-13.
  var CART_EVENT_URL = 'https://shipping-service-fjncgc2jmq-uc.a.run.app/cart-event';
  function checkoutBeacon() {
    try {
      if (!navigator.sendBeacon) return;
      navigator.sendBeacon(CART_EVENT_URL, new Blob([JSON.stringify({ sessionId: browserSessionId(), event: 'checkout' })], { type: 'text/plain' }));
    } catch (e) { /* analytics only */ }
  }

  // Check Out waits for the cart writes in flight, the "Ship date" save and the gift note save,
  // 5 seconds at most, then ALWAYS goes on (the rule from the Rebuy header, 2026-09-10).
  function checkout(button) {
    if (checkingOut) return;
    checkingOut = true;
    checkoutBeacon();
    if (button) button.classList.add('is-working');
    var gift = typeof window.psGiftNoteFlush === 'function' ? safe(window.psGiftNoteFlush) : null;
    var ready = settle().then(function () {
      // The cart is final now, so the ship date is estimated and saved for THIS cart.
      return Promise.all([typeof window.psShipDateReady === 'function' ? safe(window.psShipDateReady) : null, gift]);
    });
    Promise.race([
      ready,
      new Promise(function (r) { setTimeout(r, CHECKOUT_WAIT); })
    ]).catch(function (e) {
      console.warn('[PSCart] Check Out wait failed:', e);
    }).then(function () {
      window.location.href = ROOT + 'checkout';
      // If the browser stays (for example the shopper pressed Back), allow another click.
      setTimeout(function () {
        checkingOut = false;
        if (button) button.classList.remove('is-working');
      }, 8000);
    });
  }

  function safe(fn) {
    try {
      return Promise.resolve(fn()).catch(function (e) { console.warn('[PSCart] wait failed:', e); });
    } catch (e) {
      return Promise.resolve();
    }
  }

  // ---------------------------------------------------------------- Recommended products
  //
  // Rebuy data source 189229 had one rule: for ANY cart, return Rebuy's "Recommended (AI)"
  // endpoint, minus products already in the cart, minus products out of stock, 3 shown. When Rebuy
  // knows nothing about the cart it shows its top sellers.
  //
  // Ours (Phil, 2026-10-16): 3 places with one job each, chosen by pickRecs from 5 lists. For the 3
  // newest products in the cart, Shopify's "complementary" list (pairs Phil sets by hand in the
  // Search & Discovery app) and its "related" list (bought together, viewed together, similar),
  // taken in turn so every product in the cart gets a say; the coffee collection scored against
  // the newest coffee in the cart; the store's best sellers; and the cheapest products, for the
  // free-shipping place. An empty cart sees the best sellers and the best-selling coffee. The cards
  // come from sections/ps-cart-recs.liquid, which drops what cannot be suggested.

  // The suggestions' key: the products asked about, the products in the cart, the coffee matched.
  function recsKeyFor(items) {
    var inCart = (items || []).map(function (i) { return String(i.product_id); });
    return recInputs(items, RECS_INPUTS).join(',') + '|' + inCart.slice().sort().join(',') + '|' + coffeeLine(items);
  }

  // A cart change that changes the key retires the answer on its way and the lists on hand at once,
  // and takes away any card for a product now in the cart, before the 150 ms wait (Codex 2026-10-16).
  function scheduleRecs() {
    clearTimeout(recsTimer);
    if (recsKey !== null && recsKeyFor(cart.items) !== recsKey) {
      recsKey = null;
      recsData = null;
    }
    pruneRecs();
    recsTimer = setTimeout(loadRecs, 150);
  }

  // On every cart change: cards for products now in the cart go; with none left, so do the column,
  // the shelf and "More below".
  function pruneRecs() {
    if (!root) return;
    var inCart = {};
    (cart.items || []).forEach(function (i) { inCart[String(i.product_id)] = true; });
    var gone = false;
    $$('[data-ps-recs-list] [data-ps-rec]', root).forEach(function (c) {
      if (inCart[c.getAttribute('data-product-id')]) { c.remove(); gone = true; }
    });
    if (!gone) return;
    var left = $$('[data-ps-recs-list] [data-ps-rec]', root).length > 0;
    $('[data-ps-cart-side]', root).hidden = !left;
    $('[data-ps-cart-shelf]', root).hidden = !left;
    placeSide();
    updateMore();
  }

  function recsCacheGet(url) {
    try {
      var hit = JSON.parse(sessionStorage.getItem('ps-cart-recs:' + url) || 'null');
      if (hit && Date.now() - hit.t < RECS_TTL) return hit.html;
    } catch (e) { /* storage blocked */ }
    return null;
  }

  function recsCacheSet(url, html) {
    try {
      sessionStorage.setItem('ps-cart-recs:' + url, JSON.stringify({ t: Date.now(), html: html }));
    } catch (e) { /* full or blocked */ }
  }

  function fetchCards(url) {
    var cached = recsCacheGet(url);
    var got = cached != null
      ? Promise.resolve(cached)
      : nativeFetch(url, { credentials: 'same-origin' }).then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      }).then(function (html) {
        recsCacheSet(url, html);
        return html;
      });
    return got.then(function (html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      return $$('[data-ps-rec]', doc);
    }).catch(function () { return []; });
  }

  function loadRecs() {
    if (!root || !$('[data-ps-recs-list]', root)) return;
    var inCart = (cart.items || []).map(function (i) { return String(i.product_id); });
    var inputs = recInputs(cart.items, RECS_INPUTS);
    var key = recsKeyFor(cart.items);
    if (key === recsKey) return;
    recsKey = key;
    recsData = null;   // the lists on hand were for the old cart: never redraw them for this one

    var urls = [];
    ['complementary', 'related'].forEach(function (intent) {
      inputs.forEach(function (id) {
        urls.push(ROOT + 'recommendations/products?product_id=' + id + '&limit=10&intent=' + intent + '&section_id=' + RECS_SECTION);
      });
    });
    urls.push(ROOT + 'collections/coffee?sort_by=best-selling&section_id=' + RECS_SECTION + '&ps_coffee=' + coffeeLine(cart.items));
    urls.push(ROOT + 'collections/all?sort_by=best-selling&section_id=' + RECS_SECTION);
    urls.push(ROOT + 'collections/all?sort_by=price-ascending&section_id=' + RECS_SECTION);

    Promise.all(urls.map(fetchCards)).then(function (lists) {
      if (key !== recsKey) return; // the cart moved on while we waited
      var byId = {};
      var info = {};
      var idLists = lists.map(function (cards) {
        return cards.map(function (c) {
          var id = c.getAttribute('data-product-id');
          if (!byId[id]) {
            byId[id] = c;
            info[id] = { price: Number(c.getAttribute('data-price')) || 0, kind: c.getAttribute('data-kind') || '' };
          }
          return id;
        });
      });
      // Each product in the cart gets a turn in the pair and related lists.
      var n = inputs.length;
      var m = 2 * n;
      recsData = {
        byId: byId,
        info: info,
        inCart: inCart,
        lists: {
          pair: mergeRecs(idLists.slice(0, n), inCart, 50),
          related: mergeRecs(idLists.slice(n, m), inCart, 50),
          coffee: idLists[m],
          best: idLists[m + 1],
          cheap: idLists[m + 2]
        }
      };
      recsGap = null;
      renderPicked();
    });
  }

  // Draws the 3 places from the lists last fetched, for the free-shipping gap known now. A new
  // shipping price redraws them only when the gap changes, so the cards do not jump about.
  function renderPicked() {
    if (!recsData) return;
    var gapCents = shipInfo && shipInfo.gap > 0 ? Math.round(shipInfo.gap * 100) : null;
    recsGap = gapCents;
    var picked = pickRecs(recsData.lists, recsData.info, recsData.inCart, gapCents, RECS_LIMIT);
    renderRecs(picked.map(function (id) { return recsData.byId[id]; }));
  }

  // The same cards go to the desktop column and the phone shelf; CSS shows the one that fits.
  function renderRecs(cards) {
    $$('[data-ps-recs-list]', root).forEach(function (list) {
      list.innerHTML = '';
      cards.forEach(function (c) {
        var card = document.importNode(c, true);
        // Only the cards shown get their image (the candidates carry data-src, so they never load).
        $$('img[data-src]', card).forEach(function (img) { img.src = img.getAttribute('data-src'); });
        list.appendChild(card);
      });
    });
    $('[data-ps-cart-side]', root).hidden = cards.length === 0;
    $('[data-ps-cart-shelf]', root).hidden = cards.length === 0;
    placeSide();
    updateMore();
  }

  // PHONES: "More below: you might also like" (Phil, 2026-10-12). The shelf starts below the first
  // screen (CSS); this line shows until the shelf scrolls into view, and a tap scrolls to it. It
  // never shows on an empty cart, with no suggestions, or where the shelf is not used (desktop).
  var shelfSeen = false;
  function updateMore() {
    var more = root && $('[data-ps-cart-more]', root);
    var shelf = root && $('[data-ps-cart-shelf]', root);
    if (!more || !shelf) return;
    more.hidden = shelfSeen || shelf.hidden || !cart.item_count;
    // Says what is below in the shelf's own terms (Phil, 2026-10-12).
    var label = $('[data-ps-cart-more-label]', more);
    if (label) label.textContent = shipState === 'paid' || shipState === 'unavailable' ? 'More below: save on shipping' : 'More below: you might also like';
  }

  // The Shipping row and the Check Out label (cartSummary). Both live outside the redrawn parts, so
  // they follow the shipping card and every cart redraw.
  function renderSummary() {
    if (!root) return;
    // A changed cart never shows the old quote's total: it waits for its own (Codex 2026-10-15).
    var key = (cart.total_price || 0) + ':' + (cart.items || []).map(function (i) { return i.key + 'x' + i.quantity; }).join(',');
    if (summaryKey !== null && key !== summaryKey && shipInfo.state !== 'loading') shipInfo = { state: 'loading' };
    summaryKey = key;
    var sum = cartSummary(cart.total_price || 0, shipInfo, cart.currency);
    var row = $('[data-ps-cart-shiprow]', root);
    if (row) {
      row.hidden = !cart.item_count || sum.ship == null;
      var v = $('[data-ps-cart-shipval]', row);
      if (v) v.textContent = sum.ship || '';
    }
    // "Check out · $73.70 + tax" is drawn with Agata's round dot between the two halves.
    var label = $('[data-ps-cart-checkout-label]', root);
    if (label) {
      var halves = sum.button.split(' \u00b7 ');
      label.textContent = halves[0];
      if (halves[1]) {
        var dot = document.createElement('span');
        dot.className = 'ps-cart__dot';
        dot.setAttribute('aria-hidden', 'true');
        label.appendChild(dot);
        // A spoken separator for screen readers: "Check out, $73.70 + tax".
        var sep = document.createElement('span');
        sep.className = 'ps-cart__sr';
        sep.textContent = ', ';
        label.appendChild(sep);
        label.append(halves[1]);
      }
    }
  }
  function watchMore() {
    var more = $('[data-ps-cart-more]', root);
    var shelf = $('[data-ps-cart-shelf]', root);
    var list = $('[data-ps-cart-scroll]', root);
    if (!more || !shelf || !list) return;
    // Scroll the list itself. scrollIntoView would also scroll the clipped box around the list,
    // which left the pinned "More below" line floating 137px up (Phil's iPhone, 2026-10-12).
    // The header's shadow follows the list's scroll (Agata's scrolled frames).
    list.addEventListener('scroll', function () { root.classList.toggle('is-scrolled', list.scrollTop > 4); }, { passive: true });
    more.addEventListener('click', function () {
      var top = list.scrollTop + shelf.getBoundingClientRect().top - list.getBoundingClientRect().top;
      list.scrollTo({ top: top, behavior: 'smooth' });
    });
    if (!window.IntersectionObserver) return;
    new IntersectionObserver(function (entries) {
      shelfSeen = entries[entries.length - 1].isIntersecting;
      updateMore();
    }, { root: list, threshold: 0.2 }).observe(shelf);
  }

  // The heading follows the shipping card: "Save more on shipping" unless shipping is free.
  function setRecsCopy() {
    var copy = recsCopy(cart.item_count ? shipState : 'empty');
    $$('[data-ps-recs-title]', root).forEach(function (el) { el.textContent = copy.title; });
    $$('[data-ps-recs-line]', root).forEach(function (el) { el.textContent = copy.line; });
    placeSide();
  }

  // Desktop: the suggestions column's bottom lines up with the shipping card's bottom.
  function placeSide() {
    var side = root && $('[data-ps-cart-side]', root);
    var ship = root && $('.ps-cart__bottom', root);
    if (!side || !ship || side.hidden || !root.classList.contains('is-open')) return;
    var bottom = ship.offsetParent ? window.innerHeight - ship.getBoundingClientRect().bottom : 24;
    side.style.bottom = Math.max(16, Math.round(bottom)) + 'px';
  }

  // One "Add $21.00" button per card (Agata's version 2). The chosen size is the checked tab, the
  // list's choice, or the card's only variant.
  function recCardAction(button) {
    var card = button.closest('[data-ps-rec]');
    if (!card) return;
    var tab = $('.ps-rec__tab[aria-checked="true"]', card);
    var select = $('select', card);
    var variant = tab ? tab.getAttribute('data-value') : select ? select.value : button.getAttribute('data-variant-id');
    var label = $('[data-ps-rec-label]', button);
    var price = $('[data-ps-rec-price]', button);
    button.disabled = true;
    label.textContent = 'Adding...';
    if (price) price.hidden = true;
    // _source marks suggestion sales, so they can be measured once Rebuy's reports are gone.
    addVariant(variant, 1, { _source: 'cart-rec' }).then(function () {
      label.textContent = 'Added';
    }).catch(function (err) {
      label.textContent = 'Add';
      if (price) price.hidden = false;
      button.disabled = false;
      var msg = $('[data-ps-rec-error]', card);
      if (msg) { msg.textContent = err.message || 'Could not add this item.'; msg.hidden = false; }
      announce(err.message || 'Could not add this item.');
    });
  }

  // A size was chosen (a tab, or an option of the list): the button's price, the image and the links follow.
  function recChoose(card, choice) {
    if (!card || !choice) return;
    var value = choice.getAttribute('data-value') || choice.value;
    var price = $('[data-ps-rec-price]', card);
    if (price && choice.getAttribute('data-price-html')) price.innerHTML = choice.getAttribute('data-price-html');
    var img = $('img', card);
    if (img && choice.getAttribute('data-image')) img.src = choice.getAttribute('data-image');
    $$('a[data-ps-rec-link]', card).forEach(function (a) {
      var base = a.getAttribute('data-base');
      a.href = base + (base.indexOf('?') === -1 ? '?' : '&') + 'variant=' + value;
    });
  }

  function recTab(tab) {
    var card = tab.closest('[data-ps-rec]');
    $$('.ps-rec__tab', card).forEach(function (t) {
      var on = t === tab;
      t.setAttribute('aria-checked', String(on));
      if (on) t.removeAttribute('tabindex'); else t.setAttribute('tabindex', '-1');
    });
    recChoose(card, tab);
  }

  // ---------------------------------------------------------------- init

  function init() {
    root = document.querySelector('[data-ps-cart]');
    if (!root) return;

    root.addEventListener('click', function (e) {
      var t = e.target;
      var btn;
      if ((btn = t.closest('[data-ps-cart-close]'))) { e.preventDefault(); close(); return; }
      if ((btn = t.closest('[data-ps-qty]'))) {
        if (btn.disabled) return;
        var line = btn.closest('[data-ps-line]');
        var key = line.getAttribute('data-key');
        // Quick taps step from what the last tap asked for, not the quantity still on screen.
        var next = stepQty(lineInput(line, wantQty[key]), btn.getAttribute('data-ps-qty'));
        if (next == null) return;
        wantQty[key] = next;
        changeLine(key, next, btn);
        return;
      }
      if ((btn = t.closest('[data-ps-remove]'))) {
        changeLine(btn.closest('[data-ps-line]').getAttribute('data-key'), 0, btn);
        return;
      }
      if ((btn = t.closest('[data-ps-cart-checkout]'))) { e.preventDefault(); checkout(btn); return; }
      if ((btn = t.closest('[data-ps-rec-action]'))) { e.preventDefault(); recCardAction(btn); return; }
      if ((btn = t.closest('.ps-rec__tab'))) { e.preventDefault(); recTab(btn); }
    });
    root.addEventListener('change', function (e) {
      if (e.target.matches('[data-ps-rec] select')) recChoose(e.target.closest('[data-ps-rec]'), e.target.options[e.target.selectedIndex]);
    });
    // Arrow keys move between the size tabs, as in any radio group.
    root.addEventListener('keydown', function (e) {
      var tab = e.target.closest && e.target.closest('.ps-rec__tab');
      if (!tab || ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].indexOf(e.key) === -1) return;
      e.preventDefault();
      var tabs = $$('.ps-rec__tab', tab.parentNode);
      var i = tabs.indexOf(tab) + (e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 1);
      var next = tabs[(i + tabs.length) % tabs.length];
      recTab(next);
      next.focus();
    });
    document.addEventListener('keydown', function (e) {
      if (!root.classList.contains('is-open')) return;
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        // Esc in a phone sheet closes the sheet, not the cart.
        var sheet = openSheet();
        var x = sheet && $('[data-ps-gift-close], [data-ps-ship-sheet-close]', sheet);
        if (x) x.click(); else close();
      }
      else if (e.key === 'Tab') trapTab(e);
    });
    // The header cart icon, and any link to /cart, open the drawer instead.
    document.addEventListener('click', function (e) {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var a = e.target.closest && e.target.closest('a[href], [data-js-cart-icon]');
      if (!a || root.contains(a) || !isCartLink(a)) return;
      e.preventDefault();
      e.stopPropagation();
      open();
    }, true);
    document.addEventListener('ps-cart:shipping', function (e) {
      shipInfo = e.detail || { state: 'paid' };
      // The suggestions heading keeps its words while a new price is checked.
      if (shipInfo.state !== 'loading') shipState = shipInfo.state || 'paid';
      if (shipInfo.state !== 'loading' && recsData) {
        var g = shipInfo.gap > 0 ? Math.round(shipInfo.gap * 100) : null;
        if (g !== recsGap) renderPicked();
      }
      setRecsCopy();
      updateMore();
      renderSummary();
    });
    window.addEventListener('resize', placeSide);
    // The gift note panel and the city search change the bottom's height; the column follows.
    var bottom = $('.ps-cart__bottom', root);
    if (bottom && window.ResizeObserver) new ResizeObserver(placeSide).observe(bottom);
    // A page restored from the back/forward cache may show an old cart.
    window.addEventListener('pageshow', function (e) { if (e.persisted) track(refresh()); });

    updateCount();
    setRecsCopy();
    watchMore();
    applyRules();
    renderHold();
    scheduleRecs();
    if (/[?&]viewcart=true(&|$)/.test(window.location.search)) open();
  }

  window.PSCart = {
    items: function () { return (cart && cart.items) || []; },
    getCurrency: function () { return (cart && cart.currency) || ''; },
    cart: function () { return cart; },
    open: open,
    close: close,
    refresh: refresh,
    add: addVariant,
    change: changeLine,
    init: init,
    shippingView: shippingView,
    shipHeadline: shipHeadline,
    withinLine: withinLine,
    holdView: holdView,
    holdDelivery: holdDelivery,
    setQuotedDay: setQuotedDay,
    todayMountain: todayMountain,
    cartSummary: cartSummary,
    formatMoney: formatMoney,
    cartSessionId: cartSessionId,
    sessionId: browserSessionId,
    giftValue: giftValue,
    giftAttributes: giftAttributes
  };
})();
